import {
    useQuery,
    UseQueryResult,
    keepPreviousData,
    useSuspenseQuery,
    UseSuspenseQueryResult,
    useMutation,
    UseMutationResult,
    useQueryClient,
    QueryClient,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import { saveFile } from '@Helpers/saveFile';
import { parseMutationResponse, MutationResponse } from '../shared.types';
import {
    locationTaskDetailsResponseSchema,
    taskSearchResponseSchema,
    listFiltersResponseSchema,
    columnFiltersResponseSchema,
    locationSelectorFiltersResponseSchema,
    searchResponsibleUsersResponseSchema,
    commentsResponseSchema,
    historyResponseSchema,
    mentionableUsersResponseSchema,
    resourceAllocationsResponseSchema,
    resourceAllocationsDetailsResponseSchema,
    updateIsActiveResponseSchema,
    updateFollowingTasksResponseSchema,
    importResultSchema,
    projectCoachesResponseSchema,
    locationTaskDrawerFiltersResponseSchema,
    createFeedbackRequestSchema,
    createFeedbackResponseSchema,
    validateTranslationRequestSchema,
    validateTranslationResponseSchema,
    translateTextRequestSchema,
    translateTextResponseSchema,
    translationRatingRequestSchema,
    translationRatingResponseSchema,
    type LocationTaskDetailsResponse,
    type TaskSearchRequest,
    type TaskSearchResponse,
    type ListFiltersResponse,
    type ColumnFiltersRequest,
    type ColumnFiltersResponse,
    type LocationSelectorFiltersResponse,
    type InitialLocationSelectorFiltersRequest,
    type SearchResponsibleUsersResponse,
    type CommentsResponse,
    type HistoryResponse,
    type MentionableUsersResponse,
    type UpdateStatusRequest,
    type UpdateResponsibleUserRequest,
    type UpdateDatesRequest,
    type UpdateIsActiveRequest,
    type UpdateIsActiveResponse,
    type UpdateFollowingTasksRequest,
    type UpdateFollowingTasksResponse,
    type UpdateContingentRequest,
    type BulkShiftDatesRequest,
    type AssignCopiesRequest,
    type AssignCopiesResponse,
    type SaveCustomFiltersRequest,
    type ImportLocationTasksRequest,
    type ImportResult,
    type CreateLocationTaskRequest,
    type UpdateSavedColumnsRequest,
    type ResourceAllocationsRequest,
    type ResourceAllocationsResponse,
    type ResourceAllocationsDetailsRequest,
    type ResourceAllocationsDetailsResponse,
    type ProjectCoachesResponse,
    type LocationTaskDrawerFiltersResponse,
    type CreateFeedbackRequest,
    type CreateFeedbackResponse,
    type ValidateTranslationRequest,
    type ValidateTranslationResponse,
    type TranslateTextRequest,
    type TranslateTextResponse,
    type TranslationRatingRequest,
    type TranslationRatingResponse,
} from './locationTask.types';

// ---- Query Key Factory ----
// Structured keys enable precise, hierarchical cache invalidation.
// Use `locationTaskKeys.all` to bust everything, or a specific key to
// invalidate only what changed.

export const locationTaskKeys = {
    all: ['locationTask'] as const,

    searches: () => [...locationTaskKeys.all, 'search'] as const,
    search: (params: TaskSearchRequest) => [...locationTaskKeys.searches(), params] as const,

    listFilters: () => [...locationTaskKeys.all, 'listFilters'] as const,

    columnFilters: () => [...locationTaskKeys.all, 'columnFilters'] as const,
    columnFiltersFor: (subscriptionId: number, locationKeys: string[], allLocations = false) =>
        [...locationTaskKeys.columnFilters(), { subscriptionId, locationKeys, allLocations }] as const,

    locationSelectorFilters: (subscriptionId: number, locationKeys: string[] = []) =>
        [...locationTaskKeys.all, 'locationSelectorFilters', subscriptionId, { locationKeys }] as const,
    allLocationSelectorFilters: (subscriptionId: number) =>
        [...locationTaskKeys.all, 'locationSelectorFilters', subscriptionId, 'all'] as const,

    singles: () => [...locationTaskKeys.all, 'single'] as const,
    single: (id: number) => [...locationTaskKeys.singles(), id] as const,

    commentsAll: (taskId: number) => [...locationTaskKeys.all, 'comments', taskId] as const,
    comments: (taskId: number, pageNumber?: number, pageSize?: number) =>
        [...locationTaskKeys.commentsAll(taskId), { pageNumber, pageSize }] as const,
    history: (taskId: number) => [...locationTaskKeys.all, 'history', taskId] as const,

    responsibleUsers: (locationKeys: string[], searchTerm: string) =>
        [...locationTaskKeys.all, 'responsibleUsers', { locationKeys, searchTerm }] as const,

    mentionableUsers: (locationKey: string) =>
        [...locationTaskKeys.all, 'mentionableUsers', locationKey] as const,

    projectCoaches: (taskId: number) =>
        [...locationTaskKeys.all, 'projectCoaches', taskId] as const,

    drawerFilters: (taskId: number, locationKey: string) =>
        [...locationTaskKeys.all, 'drawerFilters', { taskId, locationKey }] as const,

    resourceAllocations: (params: ResourceAllocationsRequest) =>
        [...locationTaskKeys.all, 'resourceAllocations', params] as const,

    resourceAllocationsDetails: (params: ResourceAllocationsDetailsRequest) =>
        [...locationTaskKeys.all, 'resourceAllocationsDetails', params] as const,

    customFilter: (filterName: string) =>
        [...locationTaskKeys.all, 'customFilter', filterName] as const,
};

// ---- Invalidation Helpers ----
// Call these after mutations to keep the cache consistent.

export const invalidateSearches = (qc: QueryClient) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.searches() });

/**
 * Invalidate a single search, identified by the exact params it was made with.
 *
 * Use this instead of invalidateSearches when the caller is *changing* the search params:
 * invalidateSearches refetches every active search, which means the outgoing params still
 * mounted at that moment get refetched too — a wasted request whose result is thrown away
 * as soon as the new params render. Invalidating only the params about to be committed
 * yields exactly one request, whether or not that key is already in the cache.
 *
 * exact: true is required, not cosmetic. Partial matching walks the params object with
 * Object.keys(passed).every(...), and an empty array has no keys — so every empty array in the
 * passed params (statusIds: [], projectIds: [], … which is most of them after clearAllFilters)
 * matches any cached value, and the invalidation fans back out to the searches it must not touch.
 */
export const invalidateSearch = (qc: QueryClient, params: TaskSearchRequest) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.search(params), exact: true });

export const invalidateListFilters = (qc: QueryClient) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.listFilters() });

export const invalidateColumnFilters = (qc: QueryClient) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.columnFilters() });

export const invalidateTask = (qc: QueryClient, id: number) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.single(id) });

export const invalidateComments = (qc: QueryClient, taskId: number) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.commentsAll(taskId) });

export const invalidateHistory = (qc: QueryClient, taskId: number) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.history(taskId) });

export const invalidateDrawerFilters = (qc: QueryClient, taskId: number, locationKey: string) =>
    qc.invalidateQueries({ queryKey: locationTaskKeys.drawerFilters(taskId, locationKey) });

// ---- Search ----

const searchLocationTasks = async (params: TaskSearchRequest): Promise<TaskSearchResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}location-task/search`, { json: params }).json();
    return taskSearchResponseSchema.parse(response);
};

/**
 * How long a search result stays fresh. Without it every key change remounts an observer on a
 * result the cache already holds and refetches it immediately — so flipping between the List,
 * Gantt and Calendar tabs re-queries each time even though no filter changed. Edits made in the
 * app invalidate explicitly, so this only delays picking up changes made elsewhere.
 */
const SEARCH_STALE_TIME_MS = 60_000;

export const useSearchLocationTasks = (
    params: TaskSearchRequest,
    options?: { enabled?: boolean }
): UseQueryResult<TaskSearchResponse> =>
    useQuery<TaskSearchResponse>({
        queryKey: locationTaskKeys.search(params),
        queryFn: () => searchLocationTasks(params),
        placeholderData: keepPreviousData,
        staleTime: SEARCH_STALE_TIME_MS,
        enabled: options?.enabled,
    });

// ---- Get Single ----

const getLocationTask = async (id: number): Promise<LocationTaskDetailsResponse> => {
    const response = await createPacerApi().get(`${API_ROOT}location-task/${id}`).json();
    return locationTaskDetailsResponseSchema.parse(response);
};

export const useGetLocationTask = (
    id: number,
    options?: { enabled?: boolean }
): UseQueryResult<LocationTaskDetailsResponse> =>
    useQuery<LocationTaskDetailsResponse>({
        queryKey: locationTaskKeys.single(id),
        queryFn: () => getLocationTask(id),
        enabled: options?.enabled ?? id > 0,
        retry: false
    });

// ---- List Filters ----

const loadListFilters = async (): Promise<ListFiltersResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}location-task/location-list-filters`).json();
    return listFiltersResponseSchema.parse(response);
};

export const useLoadListFilters = (): UseSuspenseQueryResult<ListFiltersResponse> =>
    useSuspenseQuery<ListFiltersResponse>({
        queryKey: locationTaskKeys.listFilters(),
        queryFn: loadListFilters,
        // List filters change only when a user modifies column preferences; keep them fresh
        // for the session but avoid re-fetching on every window focus.
        staleTime: 5 * 60 * 1000,
    });

// ---- Column Filters ----

const loadColumnFilters = async (request: ColumnFiltersRequest): Promise<ColumnFiltersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/location-list-column-filters`, { json: request })
        .json();
    return columnFiltersResponseSchema.parse(response);
};

export const useLoadColumnFilters = (
    request: ColumnFiltersRequest,
    options?: { enabled?: boolean }
): UseQueryResult<ColumnFiltersResponse> =>
    useQuery<ColumnFiltersResponse>({
        queryKey: locationTaskKeys.columnFiltersFor(request.subscriptionId, request.locationKeys, request.allLocations),
        queryFn: () => loadColumnFilters(request),
        enabled: options?.enabled ?? (request.subscriptionId > 0),
    });

// ---- Location Selector Filters ----

const loadLocationSelectorFilters = async (
    subscriptionId: number
): Promise<LocationSelectorFiltersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/location-selector-filters`, { json: { subscriptionId } })
        .json();
    return locationSelectorFiltersResponseSchema.parse(response);
};

export const useLoadLocationSelectorFilters = (
    subscriptionId: number,
    options?: { enabled?: boolean }
): UseQueryResult<LocationSelectorFiltersResponse> =>
    useQuery<LocationSelectorFiltersResponse>({
        queryKey: locationTaskKeys.allLocationSelectorFilters(subscriptionId),
        queryFn: () => loadLocationSelectorFilters(subscriptionId),
        enabled: options?.enabled ?? subscriptionId > 0,
        staleTime: 10 * 60 * 1000,
    });

const loadInitialLocationSelectorFilters = async (
    request: InitialLocationSelectorFiltersRequest
): Promise<LocationSelectorFiltersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/initial-location-selector-filters`, { json: request })
        .json();
    return locationSelectorFiltersResponseSchema.parse(response);
};

export const useLoadInitialLocationSelectorFilters = (
    request: InitialLocationSelectorFiltersRequest,
    options?: { enabled?: boolean }
): UseQueryResult<LocationSelectorFiltersResponse> =>
    useQuery<LocationSelectorFiltersResponse>({
        queryKey: locationTaskKeys.locationSelectorFilters(request.subscriptionId, request.locationKeys ?? []),
        queryFn: () => loadInitialLocationSelectorFilters(request),
        enabled: options?.enabled ?? request.subscriptionId > 0,
        staleTime: 10 * 60 * 1000,
    });

// ---- Responsible Users ----

const searchResponsibleUsers = async (
    locationKeys: string[],
    searchTerm: string
): Promise<SearchResponsibleUsersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/search-responsible-users`, {
            json: { locationKeys, searchTerm },
        })
        .json();
    return searchResponsibleUsersResponseSchema.parse(response);
};

export const useSearchResponsibleUsers = (
    locationKeys: string[],
    searchTerm = '',
    options?: { enabled?: boolean }
): UseQueryResult<SearchResponsibleUsersResponse> =>
    useQuery<SearchResponsibleUsersResponse>({
        queryKey: locationTaskKeys.responsibleUsers(locationKeys, searchTerm),
        queryFn: () => searchResponsibleUsers(locationKeys, searchTerm),
        enabled: options?.enabled ?? locationKeys.length > 0,
    });

// ---- Comments ----

const loadComments = async (
    taskId: number,
    pageNumber?: number,
    pageSize?: number
): Promise<CommentsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/comments`, {
            json: { locationTaskId: taskId, pageNumber, pageSize },
        })
        .json();
    return commentsResponseSchema.parse(response);
};

export const useLoadComments = (
    taskId: number,
    options?: { enabled?: boolean; pageNumber?: number; pageSize?: number }
): UseQueryResult<CommentsResponse> =>
    useQuery<CommentsResponse>({
        queryKey: locationTaskKeys.comments(taskId, options?.pageNumber, options?.pageSize),
        queryFn: () => loadComments(taskId, options?.pageNumber, options?.pageSize),
        enabled: options?.enabled ?? taskId > 0,
    });

// ---- History ----

const loadHistory = async (taskId: number): Promise<HistoryResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/history`, { json: { locationTaskId: taskId } })
        .json();
    return historyResponseSchema.parse(response);
};

export const useLoadHistory = (
    taskId: number,
    options?: { enabled?: boolean }
): UseQueryResult<HistoryResponse> =>
    useQuery<HistoryResponse>({
        queryKey: locationTaskKeys.history(taskId),
        queryFn: () => loadHistory(taskId),
        enabled: options?.enabled ?? taskId > 0,
    });

// ---- Mentionable Users ----

const listMentionableUsers = async (locationKey: string): Promise<MentionableUsersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/search-mentionable-users`, { json: { locationKey } })
        .json();
    return mentionableUsersResponseSchema.parse(response);
};

export const useListMentionableUsers = (
    locationKey: string,
    options?: { enabled?: boolean }
): UseQueryResult<MentionableUsersResponse> =>
    useQuery<MentionableUsersResponse>({
        queryKey: locationTaskKeys.mentionableUsers(locationKey),
        queryFn: () => listMentionableUsers(locationKey),
        enabled: options?.enabled ?? !!locationKey,
        staleTime: 5 * 60 * 1000,
    });

// ---- Custom Filter ----

export const getCustomFilter = async (filterName: string): Promise<unknown> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}location-task/get-saved-filter/${filterName}`)
        .json();
    return response;
};

export const useGetCustomFilter = (
    filterName: string,
    options?: { enabled?: boolean }
): UseQueryResult<unknown> =>
    useQuery({
        queryKey: locationTaskKeys.customFilter(filterName),
        queryFn: () => getCustomFilter(filterName),
        enabled: options?.enabled ?? !!filterName,
    });

// ---- Resource Allocations ----

const getResourceAllocations = async (params: TaskSearchRequest): Promise<ResourceAllocationsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/resource-allocations`, { json: params })
        .json();
    return resourceAllocationsResponseSchema.parse(response);
};

export const useGetResourceAllocations = (
    params: ResourceAllocationsRequest,
    options?: { enabled?: boolean }
): UseQueryResult<ResourceAllocationsResponse> =>
    useQuery<ResourceAllocationsResponse>({
        queryKey: locationTaskKeys.resourceAllocations(params),
        queryFn: () => getResourceAllocations(params),
        placeholderData: keepPreviousData,
        enabled: options?.enabled,
    });

const getResourceAllocationsDetails = async (
    params: ResourceAllocationsDetailsRequest
): Promise<ResourceAllocationsDetailsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/resource-allocations-details`, { json: params })
        .json();
    return resourceAllocationsDetailsResponseSchema.parse(response);
};

export const useGetResourceAllocationsDetails = (
    params: ResourceAllocationsDetailsRequest,
    options?: { enabled?: boolean }
): UseQueryResult<ResourceAllocationsDetailsResponse> =>
    useQuery<ResourceAllocationsDetailsResponse>({
        queryKey: locationTaskKeys.resourceAllocationsDetails(params),
        queryFn: () => getResourceAllocationsDetails(params),
        enabled: options?.enabled,
    });

// ---- Mutations: Status / Responsible User / Dates ----

const updateStatus = async (request: UpdateStatusRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-status`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateStatus = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, UpdateStatusRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, UpdateStatusRequest>({
        mutationFn: updateStatus,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const updateResponsibleUser = async (request: UpdateResponsibleUserRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-responsible-user`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateResponsibleUser = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, UpdateResponsibleUserRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, UpdateResponsibleUserRequest>({
        mutationFn: updateResponsibleUser,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const updateDates = async (request: UpdateDatesRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-dates`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateDates = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, UpdateDatesRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, UpdateDatesRequest>({
        mutationFn: updateDates,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

// ---- Mutations: Bulk Operations ----

const updateIsActive = async (request: UpdateIsActiveRequest): Promise<UpdateIsActiveResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-isactive`, { json: request })
        .json();
    return updateIsActiveResponseSchema.parse(response);
};

export const useUpdateIsActive = (
    callback?: (result: UpdateIsActiveResponse) => void
): UseMutationResult<UpdateIsActiveResponse, Error, UpdateIsActiveRequest> => {
    const queryClient = useQueryClient();
    return useMutation<UpdateIsActiveResponse, Error, UpdateIsActiveRequest>({
        mutationFn: updateIsActive,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const updateFollowingTasks = async (
    request: UpdateFollowingTasksRequest
): Promise<UpdateFollowingTasksResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-following-tasks`, { json: request })
        .json();
    return updateFollowingTasksResponseSchema.parse(response);
};

export const useUpdateFollowingTasks = (
    callback?: (result: UpdateFollowingTasksResponse) => void
): UseMutationResult<UpdateFollowingTasksResponse, Error, UpdateFollowingTasksRequest> => {
    const queryClient = useQueryClient();
    return useMutation<UpdateFollowingTasksResponse, Error, UpdateFollowingTasksRequest>({
        mutationFn: updateFollowingTasks,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const updateContingent = async (request: UpdateContingentRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-contingent`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateContingent = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, UpdateContingentRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, UpdateContingentRequest>({
        mutationFn: updateContingent,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const bulkShiftDates = async (request: BulkShiftDatesRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/bulk-shift-dates`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useBulkShiftDates = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, BulkShiftDatesRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, BulkShiftDatesRequest>({
        mutationFn: bulkShiftDates,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

// ---- Mutations: Assign Copies ----

const assignCopies = async (request: AssignCopiesRequest): Promise<AssignCopiesResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/assign-copies`, { json: request })
        .json();
    return parseMutationResponse(response) as unknown as AssignCopiesResponse;
};

export const useAssignCopies = (
    callback?: (result: AssignCopiesResponse) => void
): UseMutationResult<AssignCopiesResponse, Error, AssignCopiesRequest> => {
    const queryClient = useQueryClient();
    return useMutation<AssignCopiesResponse, Error, AssignCopiesRequest>({
        mutationFn: assignCopies,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

// ---- Mutations: Save Task (Limited / Edit Mode) ----

const saveLocationTaskLimitedMode = async (locationTask: unknown): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .put(`${API_ROOT}location-task/update-limited-mode`, { json: locationTask })
        .json();
    return parseMutationResponse(response);
};

export const useSaveLocationTaskLimitedMode = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, unknown> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, unknown>({
        mutationFn: saveLocationTaskLimitedMode,
        onSuccess: (result, variables) => {
            callback?.(result);
            invalidateSearches(queryClient);
            const task = variables as { id?: number };
            if (task?.id) invalidateTask(queryClient, task.id);
        },
    });
};

const saveLocationTaskEditMode = async (locationTask: unknown): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .put(`${API_ROOT}location-task/update-edit-mode`, { json: locationTask })
        .json();
    return parseMutationResponse(response);
};

export const useSaveLocationTaskEditMode = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, unknown> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, unknown>({
        mutationFn: saveLocationTaskEditMode,
        onSuccess: (result, variables) => {
            callback?.(result);
            invalidateSearches(queryClient);
            const task = variables as { id?: number };
            if (task?.id) invalidateTask(queryClient, task.id);
        },
    });
};

// ---- Mutations: Create Task ----

const createLocationTask = async (request: CreateLocationTaskRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/create-location-task`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useCreateLocationTask = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, CreateLocationTaskRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, CreateLocationTaskRequest>({
        mutationFn: createLocationTask,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

// ---- Mutations: Comments ----

const createComment = async (taskId: number, comment: string): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/create-comment`, {
            json: { locationTaskId: taskId, comment },
        })
        .json();
    return parseMutationResponse(response);
};

export const useCreateComment = (
    taskId: number,
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, string> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, string>({
        mutationFn: (comment) => createComment(taskId, comment),
        onSuccess: (result) => {
            callback?.(result);
            invalidateComments(queryClient, taskId);
        },
    });
};

const updateComment = async (commentId: number, comment: string): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-comment`, { json: { commentId, comment } })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateComment = (
    taskId: number,
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, { commentId: number; comment: string }> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, { commentId: number; comment: string }>({
        mutationFn: ({ commentId, comment }) => updateComment(commentId, comment),
        onSuccess: (result) => {
            callback?.(result);
            invalidateComments(queryClient, taskId);
        },
    });
};

const deleteComment = async (commentId: number): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .delete(`${API_ROOT}location-task/delete-comment`, { json: { commentId } })
        .json();
    return parseMutationResponse(response);
};

export const useDeleteComment = (
    taskId: number,
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, number> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, number>({
        mutationFn: deleteComment,
        onSuccess: (result) => {
            callback?.(result);
            invalidateComments(queryClient, taskId);
        },
    });
};

// ---- Mutations: Links ----

const createLink = async (link: unknown): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/create-link`, { json: { link } })
        .json();
    return parseMutationResponse(response);
};

export const useCreateLink = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, unknown> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, unknown>({
        mutationFn: createLink,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const updateLink = async (link: unknown): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-link`, { json: { link } })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateLink = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, unknown> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, unknown>({
        mutationFn: updateLink,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

// ---- Mutations: Files ----

type FileRequest = Record<string, string | number | boolean | File | Blob | null | undefined>;

const buildFileFormData = (request: FileRequest): FormData => {
    const body = new FormData();
    for (const key in request) {
        const value = request[key];
        if (value === null || value === undefined) continue;
        body.append(key, value instanceof Blob ? value : String(value));
    }
    return body;
};

const uploadFile = async (request: FileRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/upload-file`, { body: buildFileFormData(request) })
        .json();
    return parseMutationResponse(response);
};

export const useUploadFile = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, FileRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, FileRequest>({
        mutationFn: uploadFile,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const updateFile = async (request: FileRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-file`, { body: buildFileFormData(request) })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateFile = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, FileRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, FileRequest>({
        mutationFn: updateFile,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const deleteFile = async (request: FileRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/delete-file`, { body: buildFileFormData(request) })
        .json();
    return parseMutationResponse(response);
};

export const useDeleteFile = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, FileRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, FileRequest>({
        mutationFn: deleteFile,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

// ---- Mutations: Import / Export ----

const importLocationTasks = async (request: ImportLocationTasksRequest): Promise<ImportResult> => {
    const body = new FormData();
    body.append('file', request.file);
    body.append('subscriptionId', String(request.subscriptionId));
    body.append('locationKey', request.locationKey);

    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/import`, { body })
        .json();
    return importResultSchema.parse(response);
};

export const useImportLocationTasks = (
    callback?: (result: ImportResult) => void
): UseMutationResult<ImportResult, Error, ImportLocationTasksRequest> => {
    const queryClient = useQueryClient();
    return useMutation<ImportResult, Error, ImportLocationTasksRequest>({
        mutationFn: importLocationTasks,
        onSuccess: (result) => {
            callback?.(result);
            invalidateSearches(queryClient);
        },
    });
};

const downloadImportTemplate = async (locationKey?: string): Promise<void> => {
    const endpoint = locationKey
        ? `${API_ROOT}location-task/import-template?locationKey=${encodeURIComponent(locationKey)}`
        : `${API_ROOT}location-task/import-template`;
    const response = await createPacerApi().get(endpoint);
    await saveFile(response);
};

export const useDownloadImportTemplate = (
    callback?: () => void
): UseMutationResult<void, Error, string | undefined> =>
    useMutation<void, Error, string | undefined>({
        mutationFn: downloadImportTemplate,
        onSuccess: () => callback?.(),
    });

// ---- Mutations: Column Management ----

const updateSavedColumns = async (request: UpdateSavedColumnsRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/location-list-save-columns`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateSavedColumns = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, UpdateSavedColumnsRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, UpdateSavedColumnsRequest>({
        mutationFn: updateSavedColumns,
        onSuccess: (result) => {
            callback?.(result);
            invalidateListFilters(queryClient);
        },
    });
};

// ---- Mutations: Custom Filters ----

const saveCustomFilters = async (request: SaveCustomFiltersRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/save-filters`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useSaveCustomFilters = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, SaveCustomFiltersRequest> =>
    useMutation<MutationResponse, Error, SaveCustomFiltersRequest>({
        mutationFn: saveCustomFilters,
        onSuccess: (result) => callback?.(result),
    });

const deleteCustomFilter = async (filterName: string): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .delete(`${API_ROOT}location-task/user-filter`, { json: { filterName } })
        .json();
    return parseMutationResponse(response);
};

export const useDeleteCustomFilter = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, string> =>
    useMutation<MutationResponse, Error, string>({
        mutationFn: deleteCustomFilter,
        onSuccess: (result) => callback?.(result),
    });

// ---- Project Coaches ----

const loadProjectCoaches = async (taskId: number): Promise<ProjectCoachesResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/project-coaches`, { json: { locationTaskId: taskId } })
        .json();
    return projectCoachesResponseSchema.parse(response);
};

export const useLoadProjectCoaches = (
    taskId: number,
    options?: { enabled?: boolean }
): UseQueryResult<ProjectCoachesResponse> =>
    useQuery<ProjectCoachesResponse>({
        queryKey: locationTaskKeys.projectCoaches(taskId),
        queryFn: () => loadProjectCoaches(taskId),
        enabled: options?.enabled ?? taskId > 0,
    });

// ---- Location Task Drawer Filters ----

const loadLocationTaskDrawerFilters = async (
    taskId: number,
    locationKey: string
): Promise<LocationTaskDrawerFiltersResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/location-task-drawer-filters`, {
            json: { locationTaskId: taskId, locationKey },
        })
        .json();
    return locationTaskDrawerFiltersResponseSchema.parse(response);
};

export const useLoadLocationTaskDrawerFilters = (
    taskId: number,
    locationKey: string,
    options?: { enabled?: boolean }
): UseQueryResult<LocationTaskDrawerFiltersResponse> =>
    useQuery<LocationTaskDrawerFiltersResponse>({
        queryKey: locationTaskKeys.drawerFilters(taskId, locationKey),
        queryFn: () => loadLocationTaskDrawerFilters(taskId, locationKey),
        enabled: options?.enabled ?? (taskId >= 0 && !!locationKey),
    });

// ---- Feedback ----

const createFeedback = async (request: CreateFeedbackRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/create-feedback`, { json: request })
        .json();
    return parseMutationResponse(response);
};

export const useCreateFeedback = (
    callback?: (result: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, CreateFeedbackRequest> =>
    useMutation<MutationResponse, Error, CreateFeedbackRequest>({
        mutationFn: createFeedback,
        onSuccess: (result) => callback?.(result),
    });

// ---- Translation ----

const validateTranslation = async (request: ValidateTranslationRequest): Promise<ValidateTranslationResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}location-task/translation/validate/${request.locationTaskId}?language=${request.language}`)
        .json();
    return validateTranslationResponseSchema.parse(response);
};

export const useValidateTranslation = (
    callback?: (result: ValidateTranslationResponse) => void
): UseMutationResult<ValidateTranslationResponse, Error, ValidateTranslationRequest> =>
    useMutation<ValidateTranslationResponse, Error, ValidateTranslationRequest>({
        mutationFn: validateTranslation,
        onSuccess: (result) => callback?.(result),
    });

const translateText = async (request: TranslateTextRequest): Promise<TranslateTextResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/translation`, { json: request })
        .json();
    return translateTextResponseSchema.parse(response);
};

export const useTranslateText = (
    callback?: (result: TranslateTextResponse) => void
): UseMutationResult<TranslateTextResponse, Error, TranslateTextRequest> =>
    useMutation<TranslateTextResponse, Error, TranslateTextRequest>({
        mutationFn: translateText,
        onSuccess: (result) => callback?.(result),
    });

const translationRating = async (request: TranslationRatingRequest): Promise<TranslationRatingResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/translation/rating`, { json: request })
        .json();
    return translationRatingResponseSchema.parse(response);
};

export const useTranslationRating = (
    callback?: (result: TranslationRatingResponse) => void
): UseMutationResult<TranslationRatingResponse, Error, TranslationRatingRequest> =>
    useMutation<TranslationRatingResponse, Error, TranslationRatingRequest>({
        mutationFn: translationRating,
        onSuccess: (result) => callback?.(result),
    });
